import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal/Reveal";
import heroImage from "../assets/images/crop-farming.png";
import certImage from "../assets/images/about-biogas-plant.jpg";
import {
  IconShieldCheck,
  IconLeafRegs,
  IconRefresh,
  IconTransfer,
  IconReport,
  IconGlobeLeaf,
  IconCertificate,
  IconCheckCircle,
  IconArrowRight,
  IconCompass,
  IconLayers,
  IconTarget,
  IconPhone,
  IconMail,
  IconLocation,
} from "../components/Icons/Icons";
import "../assets/css/services-page.css";

const STATS = [
  { value: "6", label: "Core service areas" },
  { value: "ISCC EU & PLUS", label: "Certification support" },
  { value: "UK & EU", label: "Regulatory coverage" },
  { value: "End-to-end", label: "Accreditation to assurance" },
];

const SERVICES = [
  {
    icon: IconShieldCheck,
    title: "Ofgem & Regulatory Compliance",
    text: "Meeting Ofgem's requirements can be time-consuming and complex. We provide end-to-end support, from pre-accreditation through to ongoing compliance and audit preparation.",
    items: [
      "Pre-accreditation and application support",
      "Green Gas Support Scheme (GGSS) & Renewable Heat Incentive (RHI)",
      "Monthly ROC / REGO and quarterly ND-RHI / GGSS submissions",
      "Problem cases, including meter changes and audit support",
    ],
  },
  {
    icon: IconLeafRegs,
    title: "Green Gas Certificate Scheme",
    text: "We manage your ongoing GGCS obligations so your green gas is properly certified, tracked and reported quarter after quarter.",
    items: [
      "Quarterly gas volume submissions",
      "Certificate issuance and tracking",
      "Registry account maintenance",
    ],
  },
  {
    icon: IconRefresh,
    title: "RTFO Compliance",
    text: "Support for the Renewable Transport Fuel Obligation, from verification readiness through to reporting and credit management.",
    items: [
      "RTFO verification readiness and audit support",
      "Reporting and RTFC (credit) management",
      "Mass balance and GHG calculations for transport fuels",
    ],
  },
  {
    icon: IconTransfer,
    title: "Nabisy Transfers",
    text: "End-to-end handling of Proof of Sustainability documentation and transfers for EU markets, keeping your declarations moving smoothly.",
    items: [
      "Proof of Sustainability (PoS) creation and transfer",
      "Mass balance reconciliation between operators",
      "Registry account administration",
    ],
  },
  {
    icon: IconReport,
    title: "SECR Reporting",
    text: "Streamlined Energy & Carbon Reporting prepared accurately and on time, turning your energy and emissions data into clear, compliant disclosures.",
    items: [
      "Annual energy and carbon data collection",
      "GHG emissions calculations (Scope 1 & 2)",
      "Energy-efficiency narrative and reporting",
    ],
  },
  {
    icon: IconGlobeLeaf,
    title: "Sustainability & Net Zero",
    text: "Guidance across the emerging regulations shaping the sector, helping you stay ahead of change and plan with confidence.",
    items: [
      "UK Emissions Trading Scheme (UK ETS)",
      "SAF Mandate",
      "DfT credits",
      "Net zero strategy and reporting support",
    ],
  },
];

const CERT_SCOPE = [
  "GAP analyses",
  "Internal audits",
  "Mass balance construction",
  "GHG calculations",
  "Supply-chain traceability",
  "Proof of Sustainability support",
];

const STEPS = [
  {
    icon: IconCompass,
    step: "01",
    title: "Understand",
    text: "We assess your feedstocks, schemes and obligations to map a clear route to compliance.",
  },
  {
    icon: IconLayers,
    step: "02",
    title: "Implement",
    text: "We build your mass balance and GHG calculations and guide you through certification and accreditation.",
  },
  {
    icon: IconTarget,
    step: "03",
    title: "Report & assure",
    text: "We handle ongoing submissions, audit preparation and independent assurance, quarter after quarter.",
  },
];

function ServicesPage() {
  return (
    <main className="services-page">
      {/* ---------- Hero ---------- */}
      <section className="sv-hero">
        <div className="sv-hero-backdrop" aria-hidden="true">
          <span className="sv-glow sv-glow-teal" />
          <span className="sv-glow sv-glow-green" />
        </div>

        <div className="container sv-hero-inner">
          <div className="sv-hero-content">
            <Reveal direction="up">
              <span className="section-label">Our Services</span>
            </Reveal>
            <Reveal direction="up" delay={90}>
              <h1 className="sv-hero-title">
                Specialist support across{" "}
                <span className="sv-accent">every scheme</span>
              </h1>
            </Reveal>
            <Reveal direction="up" delay={170}>
              <p className="sv-hero-lead">
                From certification and accreditation to ongoing reporting, we
                provide practical, audit-ready support across the frameworks
                that shape the renewable fuels, bioenergy and low-carbon energy
                sectors.
              </p>
            </Reveal>
            <Reveal direction="up" delay={250}>
              <div className="sv-hero-actions">
                <Link to="/contact" className="btn btn-primary">
                  Talk to our team <IconArrowRight width={17} height={17} />
                </Link>
                <Link to="/compliance" className="btn btn-secondary">
                  View compliance
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal direction="right" delay={140}>
            <figure className="sv-hero-visual">
              <div className="sv-hero-frame">
                <img
                  src={heroImage}
                  alt="Aerial view of a tractor spraying a green crop field"
                />
                <div className="sv-hero-overlay" />
                <div className="sv-hero-badge">
                  <span className="sv-hero-badge-icon">
                    <IconShieldCheck width={20} height={20} />
                  </span>
                  <span className="sv-hero-badge-text">
                    <strong>End-to-End Support</strong>
                    Certification to reporting
                  </span>
                </div>
              </div>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---------- Stats band ---------- */}
      <section className="sv-stats">
        <div className="container sv-stats-grid">
          {STATS.map((stat, index) => (
            <Reveal direction="up" delay={index * 80} key={stat.label}>
              <div className="sv-stat">
                <span className="sv-stat-value">{stat.value}</span>
                <span className="sv-stat-label">{stat.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Detailed services ---------- */}
      <section className="section section-white">
        <div className="container">
          <div className="section-header">
            <Reveal direction="up">
              <span className="section-label">Our Services</span>
              <h2 className="section-title">What We Do</h2>
              <p className="section-subtitle">
                Six core areas of support, covering the certification,
                compliance and reporting obligations that keep renewable energy
                businesses trading with confidence.
              </p>
            </Reveal>
          </div>

          <div className="sv-grid">
            {SERVICES.map((service, index) => {
              const Icon = service.icon;
              return (
                <Reveal
                  direction="up"
                  delay={(index % 2) * 90}
                  key={service.title}
                >
                  <article className="sv-card card">
                    <div className="sv-card-head">
                      <span className="sv-card-icon">
                        <Icon width={26} height={26} />
                      </span>
                      <span className="sv-card-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="sv-card-title">{service.title}</h3>
                    <p className="sv-card-text">{service.text}</p>
                    <ul className="sv-card-list">
                      {service.items.map((item) => (
                        <li key={item}>
                          <span className="sv-card-check">
                            <IconCheckCircle width={15} height={15} />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Certification highlight ---------- */}
      <section className="section section-tint">
        <div className="container split-layout">
          <Reveal direction="left">
            <div className="sv-cert-image">
              <img
                src={certImage}
                alt="Twin anaerobic digester tanks at a biogas plant"
              />
              <div className="sv-cert-badge">
                <IconCertificate width={20} height={20} />
                <span>ISCC EU · ISCC PLUS · GGCS</span>
              </div>
            </div>
          </Reveal>

          <Reveal direction="right">
            <div className="sv-cert-content">
              <span className="section-label">Certification Support</span>
              <h2 className="section-title">
                Audit-ready certification, end to end
              </h2>
              <p className="sv-para">
                Underpinning every service is a complete certification toolkit.
                We help you reach and maintain ISCC EU, ISCC PLUS and GGCS
                certification with practical, hands-on support at every stage.
              </p>
              <ul className="sv-cert-list">
                {CERT_SCOPE.map((item) => (
                  <li key={item}>
                    <IconCheckCircle
                      className="sv-cert-check"
                      width={17}
                      height={17}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-primary sv-cert-cta">
                Discuss certification <IconArrowRight width={17} height={17} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- How we work ---------- */}
      <section className="section section-white">
        <div className="container">
          <div className="section-header">
            <Reveal direction="up">
              <span className="section-label">How We Work</span>
              <h2 className="section-title">A clear path to compliance</h2>
              <p className="section-subtitle">
                However complex your obligations, we keep the journey simple:
                three straightforward stages from first conversation to ongoing
                assurance.
              </p>
            </Reveal>
          </div>

          <div className="sv-steps">
            {STEPS.map((item, index) => {
              const Icon = item.icon;
              return (
                <React.Fragment key={item.title}>
                  {index > 0 && (
                    <span className="sv-step-connector" aria-hidden="true">
                      <IconArrowRight width={22} height={22} />
                    </span>
                  )}
                  <Reveal direction="up" delay={index * 100}>
                    <div className="sv-step card">
                      <span className="sv-step-icon">
                        <Icon width={24} height={24} />
                      </span>
                      <span className="sv-step-num">{item.step}</span>
                      <h3 className="sv-step-title">{item.title}</h3>
                      <p className="sv-step-text">{item.text}</p>
                    </div>
                  </Reveal>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="sv-cta">
        <div className="container">
          <div className="sv-cta-card">
            <span className="sv-cta-dots" aria-hidden="true" />
            <span className="sv-cta-blob" aria-hidden="true" />

            <div className="sv-cta-inner">
              <Reveal direction="up">
                <span className="section-label sv-cta-label">Get in Touch</span>
                <h2 className="sv-cta-title">
                  Not sure which service you need?
                </h2>
                <p className="sv-cta-text">
                  Tell us about your business and we will point you to the right
                  support, from certification and accreditation to ongoing
                  reporting and assurance.
                </p>
                <div className="sv-cta-actions">
                  <a href="tel:01984624989" className="btn btn-primary">
                    <IconPhone width={17} height={17} /> Call 01984 624989
                  </a>
                  <a
                    href="mailto:eviegarner@sustainabilityadvisors.co.uk"
                    className="btn btn-outline-light"
                  >
                    <IconMail width={17} height={17} /> Email Us
                  </a>
                </div>
                <p className="cta-address">
                  <IconLocation width={16} height={16} /> Higher Ford, Wiveliscombe, Taunton, Somerset, TA4 2RL
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ServicesPage;
